import { Camera, Archive, ArchiveRestore, Pencil, Trash2, CircleDollarSign, Check, CheckSquare, Square, AlertTriangle, Lock, RotateCcw, Mail, ChevronDown } from "lucide-react";
import { cn, formatTimeAgo } from "../../lib/utils";
import { SERVICE_LABEL, STATUS_COLORS, STATUS_LABEL } from "../../lib/constants";
import TowAuthorityCell from "../TowAuthorityCell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { salvageFull, salvageShort, tenantLocale, tenantTz } from "../../lib/company";

// The states a person is entitled to decide. "Paid" and "Signed Over" are
// facts the app derives from what happened to the money and the vehicle —
// see billing_state.py. Until Jun 2026 the pill could only flip between the
// first two, so Non Chargeable and Written Off existed in the API and were
// unreachable from any screen (0 jobs had ever used them).
const BILLING_CHOICES = [
  { value: "to_invoice", label: "To Invoice", hint: "Owed, nothing raised yet" },
  { value: "invoice_sent", label: "Invoice Sent", hint: "Invoice has gone out" },
  { value: "non_chargeable", label: "Non Chargeable", hint: "Nothing to bill — courtesy, own vehicle, $0 release" },
  { value: "written_off", label: "Written Off", hint: "Bad debt — given up on" },
];

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  return d.toLocaleString(tenantLocale(), { timeZone: tenantTz(),
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

/**
 * A single row in the History table.
 * Heavy data-testid coverage because the testing agent + back-office manual
 * workflow both rely on these to drive specific rows.
 *
 * Renders conditional columns based on role flags passed from HistoryPage
 * (canEdit / canDelete / canArchive / canSetTowAuthority + archivedView).
 * Keeps callbacks lifted to the parent so optimistic state updates stay
 * centralised.
 */
export default function HistoryRow({
  job,
  truck,
  isSelected,
  isAdmin,
  archivedView,
  canEdit,
  canDelete,
  canArchive,
  canSetBilling,
  canSignToNts,
  canSetTowAuthority,
  canRestore,
  togglingInvoiced,
  onClick,
  onSelect,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  onSetBillingState,
  onTowAuthoritySaved,
  onSignToNts,
  onRestore,
}) {
  const c = STATUS_COLORS[job.status] || STATUS_COLORS.completed;
  // Everything except "To Invoice" can be archived — see billing_state.py.
  const blockedArchive = !isAdmin && job.billing_state === "to_invoice";

  return (
    <tr
      className={cn(
        "border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors",
        isSelected && "bg-violet-50 dark:bg-violet-950/30"
      )}
      onClick={onClick}
      data-testid={`history-row-${job.id}`}
    >
      {canArchive && !archivedView && (
        <td className="px-2 py-2 w-8" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onSelect}
            className="text-slate-400 hover:text-violet-600 transition-colors"
            aria-label={isSelected ? "Deselect job" : "Select job"}
            data-testid={`history-select-${job.id}`}
          >
            {isSelected ? <CheckSquare className="h-4 w-4 text-violet-600" /> : <Square className="h-4 w-4" />}
          </button>
        </td>
      )}
      <td className="px-2 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">
        <div className="font-semibold">{formatDateTime(job.completed_at || job.updated_at)}</div>
        <div className="text-xs text-slate-400">{formatTimeAgo(job.completed_at || job.updated_at)}</div>
      </td>
      <td className="px-2 py-2">
        <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase", c.bg, c.text)}>
          <span className={cn("h-1.5 w-1.5 rounded-full", c.dot)} />
          {STATUS_LABEL[job.status]}
        </span>
      </td>
      <td className="px-2 py-2">
        <div className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
          {job.customer_name}
          {(job.photos?.length || 0) > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200 px-1.5 py-0.5 rounded"
              title={`${job.photos.length} damage photo${job.photos.length === 1 ? "" : "s"}`}
              data-testid={`history-photos-badge-${job.id}`}
            >
              <Camera className="h-2.5 w-2.5" /> {job.photos.length}
            </span>
          )}
          {job.archived_at && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-800 bg-violet-100 dark:bg-violet-900/40 dark:text-violet-200 px-1.5 py-0.5 rounded"
              title={`Archived ${new Date(job.archived_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" })}${job.archived_by ? ` by ${job.archived_by}` : ""}`}
              data-testid={`history-archived-badge-${job.id}`}
            >
              <Archive className="h-2.5 w-2.5" /> Archived
            </span>
          )}
          {job.retroactive_entry && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-800 bg-purple-100 dark:bg-purple-900/40 dark:text-purple-200 px-1.5 py-0.5 rounded"
              title={`Retroactive entry — logged after the fact${job.retroactive_entered_by ? ` by ${job.retroactive_entered_by}` : ""}${job.retroactive_entered_at ? ` on ${new Date(job.retroactive_entered_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" })}` : ""}`}
              data-testid={`history-retro-badge-${job.id}`}
            >
              Retro
            </span>
          )}
          {job.cancelled_on_route && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded",
                job.cancel_on_route_charge
                  ? "text-emerald-800 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200"
                  : "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
              )}
              title={`Cancelled on Route — ${job.cancel_on_route_charge ? "chargeable (bill at standard rate for the job type)" : "no charge"}${job.cancel_on_route_by ? ` · by ${job.cancel_on_route_by}` : ""}`}
              data-testid={`history-cor-badge-${job.id}`}
            >
              C-o-R {job.cancel_on_route_charge ? "· Charge" : "· No charge"}
            </span>
          )}
          {job.auto_closed_source === "aa_deallocated" && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-800 bg-rose-100 dark:bg-rose-900/40 dark:text-rose-200 px-1.5 py-0.5 rounded"
              title="AA walked this job's state back to pre-claim. Use the Restore button to bring it back to the dispatch queue if AA actually did the job."
              data-testid={`history-aa-dealloc-badge-${job.id}`}
            >
              AA Deallocated
            </span>
          )}
          {job.last_emailed_at && (() => {
            // "Emailed" badge (July 2026) — surfaces the last insurer email
            // so dispatchers see at a glance the report already went out
            // and don't accidentally double-send. Full audit is in
            // `sent_emails`; these are the last-send snapshot fields.
            const recipients = (job.last_emailed_to || []).join(", ");
            const sendCount = job.email_send_count || 1;
            const isMulti = sendCount > 1;
            const badgeClass = isMulti
              ? "text-amber-800 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-200"
              : "text-sky-800 bg-sky-100 dark:bg-sky-900/40 dark:text-sky-200";
            const tooltip = `Report emailed ${formatTimeAgo(job.last_emailed_at)}${
              recipients ? ` to ${recipients}` : ""
            }${isMulti ? ` · sent ${sendCount}× so far — check before sending again` : ""}${
              job.last_emailed_by ? ` · by ${job.last_emailed_by}` : ""
            }`;
            return (
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded",
                  badgeClass,
                )}
                title={tooltip}
                data-testid={`history-emailed-badge-${job.id}`}
              >
                <Mail className="h-2.5 w-2.5" /> Emailed{isMulti ? ` ${sendCount}×` : ""}
              </span>
            );
          })()}
        </div>
        {job.customer_phone && (
          <a
            href={`tel:${job.customer_phone.replace(/[^+\d]/g, "")}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-blue-700 dark:text-blue-300 hover:underline"
          >
            {job.customer_phone}
          </a>
        )}
      </td>
      <td className="px-2 py-2 text-slate-700 dark:text-slate-300 max-w-[190px]">
        <div className="break-words">{job.vehicle || "—"}</div>
        {job.rego && <div className="text-xs font-mono text-slate-500">{job.rego}</div>}
      </td>
      <td className="px-2 py-2 text-slate-700 dark:text-slate-300">
        {SERVICE_LABEL[job.service_type] || job.service_type}
      </td>
      <td className="px-2 py-2 text-slate-700 dark:text-slate-300">
        {truck?.name || <span className="text-slate-300 dark:text-slate-600">unassigned</span>}
      </td>
      <td className="px-2 py-2 text-right text-slate-700 dark:text-slate-300 whitespace-nowrap font-mono">
        {job.round_trip_km != null ? (
          job.km_review_needed && !job.km_confirmed_by ? (
            <span
              className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 cursor-help"
              title={
                (job.km_review_reason || "Round-trip km looks unusually high — please review.")
                + " Open the job to Confirm or Recalculate."
              }
              data-testid={`history-rtk-review-${job.id}`}
            >
              <span aria-hidden="true">⚠</span>
              <span>{job.round_trip_km.toFixed(1)} km</span>
            </span>
          ) : (
            <span data-testid={`history-rtk-${job.id}`}>{job.round_trip_km.toFixed(1)} km</span>
          )
        ) : (
          // No km — nudge the user to open the job and hit Recalculate.
          // Grey em-dash was silent; this at least tells them there's an
          // action available.
          <span
            className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-500 cursor-help"
            title="Round-trip km not calculated. Open the job to Recalculate."
            data-testid={`history-rtk-missing-${job.id}`}
          >
            <span aria-hidden="true">—</span>
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-slate-600 dark:text-slate-400 text-[11px] leading-tight">
        {job.work_order_number && <div>WO {job.work_order_number}</div>}
        {job.aa_job_id && <div>AA {job.aa_job_id}</div>}
        {!job.work_order_number && !job.aa_job_id && job.source && (
          <div className="text-slate-400 dark:text-slate-500 uppercase">
            {job.source === "manual" ? salvageShort() : job.source}
          </div>
        )}
      </td>
      {canSetTowAuthority && (
        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
          <TowAuthorityCell
            job={job}
            disabled={!!job.archived_at}
            testIdPrefix="history-tow-authority"
            onSaved={onTowAuthoritySaved}
          />
        </td>
      )}
      {/* Billing state pill. Oct 2026 — was a red/green "To be Invoiced /
          Invoiced" tick named after Xero, which the app has never been
          connected to. It now shows where the money on this job actually is.
          Facts (Paid at the counter, Signed Over) are read-only: nobody gets
          to declare those. Only "To Invoice" blocks archiving.
          Locked while the vehicle is still in a yard — fees aren't final
          until it's released (On-tow out / Sign Over / Released DVA). */}
      {canSetBilling && (() => {
        const state = job.billing_state;
        const yardLocked = !!job.storage_started_at
          && !job.storage_stopped_at
          && state !== "invoice_sent";
        const yardLabel = job.storage_yard
          ? job.storage_yard.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
          : "yard";
        // Facts stay read-only: nobody declares a job Paid or Signed Over,
        // and nothing is final while the vehicle is still in a yard.
        const settable = !yardLocked && BILLING_CHOICES.some((c) => c.value === state);
        const STYLES = {
          to_invoice: "bg-rose-500 hover:bg-rose-600 active:bg-rose-700 border-rose-600 text-white",
          invoice_sent: "bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 border-emerald-600 text-white",
          paid: "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200",
          non_chargeable: "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300",
          signed_over: "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200",
          written_off: "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200",
        };
        const CONTENT = {
          to_invoice: (<><CircleDollarSign className="h-3.5 w-3.5" /> To Invoice</>),
          invoice_sent: (<><Check className="h-3.5 w-3.5" /> Invoice Sent</>),
          paid: (<><Check className="h-3.5 w-3.5" /> Paid</>),
          non_chargeable: (<>Non Chargeable</>),
          signed_over: (<>Signed Over</>),
          written_off: (<>Written Off</>),
        };
        const TITLES = {
          to_invoice: "Owed, nothing raised yet — click once the invoice has been raised. Blocks archiving.",
          invoice_sent: `Invoice sent${job.invoiced_by ? ` by ${job.invoiced_by}` : ""}${job.invoiced_at ? ` — ${formatDateTime(job.invoiced_at)}` : ""}. Click to revert.`,
          paid: "Paid at the counter — recorded on release.",
          non_chargeable: "Nothing to bill on this job.",
          signed_over: "Vehicle taken in lieu of payment.",
          written_off: "Written off as bad debt.",
        };
        return (
          <td className="px-2 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
            {state ? (
              settable ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      disabled={togglingInvoiced}
                      className={cn(
                        "inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md transition-colors border-2",
                        STYLES[state] || STYLES.non_chargeable,
                        togglingInvoiced && "opacity-60 cursor-wait",
                      )}
                      title={TITLES[state] || ""}
                      data-testid={`history-invoice-toggle-${job.id}`}
                    >
                      {CONTENT[state] || state}
                      <ChevronDown className="h-3 w-3 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56" data-testid={`history-billing-menu-${job.id}`}>
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                      Where's the money on this job?
                    </DropdownMenuLabel>
                    {BILLING_CHOICES.map((choice) => (
                      <DropdownMenuItem
                        key={choice.value}
                        disabled={state === choice.value}
                        onSelect={() => onSetBillingState?.(job, choice.value)}
                        data-testid={`history-billing-${choice.value}-${job.id}`}
                      >
                        <div>
                          <div className="font-semibold flex items-center gap-1.5">
                            {state === choice.value && <Check className="h-3 w-3" />}
                            {choice.label}
                          </div>
                          <div className="text-[11px] text-slate-500">{choice.hint}</div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  disabled
                  className={cn(
                    "inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md border-2 cursor-default",
                    yardLocked
                      ? "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed"
                      : STYLES[state] || STYLES.non_chargeable,
                  )}
                  title={
                    yardLocked
                      ? `Vehicle still in ${yardLabel}. Release via On-tow out, Sign Over, or Released DVA before invoicing so tow + km + storage fees are finalised.`
                      : TITLES[state] || ""
                  }
                  data-testid={`history-invoice-toggle-${job.id}`}
                >
                  {yardLocked
                    ? (<><Lock className="h-3.5 w-3.5" /> In {yardLabel}</>)
                    : CONTENT[state] || state}
                </button>
              )
            ) : (
              <span className="text-slate-300 dark:text-slate-600 text-xs">—</span>
            )}
          </td>
        );
      })()}
      {(canEdit || canDelete || canArchive || canSignToNts) && (
        <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          {canRestore && job.status === "cancelled" && (
            <button
              onClick={() => onRestore?.(job)}
              className="text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 p-1 rounded transition-colors mr-0.5"
              title={
                job.auto_closed_source === "aa_deallocated"
                  ? "Restore from AA deallocation — put this job back into the pending dispatch queue"
                  : "Restore — put this job back into the pending dispatch queue"
              }
              aria-label="Restore cancelled job to queue"
              data-testid={`history-restore-job-${job.id}`}
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => onEdit(job)}
              className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 p-1 rounded transition-colors mr-0.5"
              title="Edit job for invoicing"
              aria-label="Edit job"
              data-testid={`history-edit-${job.id}`}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {canSignToNts && !archivedView && !job.released_to_nts_at && (
            <button
              onClick={() => onSignToNts?.(job)}
              className="text-slate-400 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/40 p-1 rounded transition-colors mr-0.5"
              title={`Transfer to ${salvageShort()} in lieu of unpaid tow & storage`}
              aria-label={`Sign over to ${salvageShort()}`}
              data-testid={`history-sign-nts-${job.id}`}
            >
              <AlertTriangle className="h-4 w-4" />
            </button>
          )}
          {canArchive && !archivedView && (
            <button
              onClick={() => !blockedArchive && onArchive(job)}
              disabled={blockedArchive}
              className={cn(
                "p-1 rounded transition-colors mr-0.5",
                blockedArchive
                  ? "text-slate-300 dark:text-slate-700 cursor-not-allowed"
                  : "text-slate-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30",
              )}
              title={
                blockedArchive
                  ? "Still to invoice — mark it Invoice Sent or Non Chargeable before archiving"
                  : "Archive — settled and ready to put away"
              }
              aria-label="Archive job"
              data-testid={`history-archive-${job.id}`}
            >
              <Archive className="h-4 w-4" />
            </button>
          )}
          {canArchive && archivedView && (
            <button
              onClick={() => onUnarchive(job)}
              className="text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 p-1 rounded transition-colors mr-0.5"
              title="Restore — bring back to the active completed list"
              aria-label="Unarchive job"
              data-testid={`history-unarchive-${job.id}`}
            >
              <ArchiveRestore className="h-4 w-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(job)}
              className="text-slate-400 hover:text-rose-600 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 p-1 rounded transition-colors"
              title="Delete from history"
              aria-label="Delete from history"
              data-testid={`history-delete-${job.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

// Re-export so HistoryPage can format the invoice tooltip timestamp the same way.
export { formatDateTime };
